import type { Activity, Goal } from '../api/client';
import { getLocalDateStr } from './format';

/**
 * The status a user should SEE, derived from the stored status and the clock.
 *
 * design.md says an activity is "in progress" once its start time has passed
 * and "todo" while it is still ahead — status is a function of time. The app
 * used to make that true by WRITING the transition from every device on a
 * 60-second timer. Two devices then raced each other, and a phone waking from
 * the background would overwrite a completion the laptop had just made. So
 * nothing writes these transitions any more: todo/in-progress is derived here,
 * at render time, and only done/skipped (and an explicit manual choice) are
 * ever stored.
 */
export function deriveActivityStatus(
  activity: Pick<Activity, 'status' | 'start_time'>,
  now: Date = new Date(),
): Activity['status'] {
  const { status, start_time } = activity;
  if (!start_time) return status;
  const start = new Date(start_time);
  if (Number.isNaN(start.getTime())) return status;
  if (status === 'todo' && start <= now) return 'in-progress';
  if (status === 'in-progress' && start > now) return 'todo';
  return status;
}

/**
 * Same idea for goals: planned until the (decrypted, YYYY-MM-DD) start date
 * arrives, active from then on. Completed and paused are the user's call and
 * pass through untouched.
 */
export function deriveGoalStatus(
  status: Goal['status'],
  startDate: string | null | undefined,
  today: string = getLocalDateStr(),
): Goal['status'] {
  if (!startDate) return status;
  if (status === 'planned' && startDate <= today) return 'active';
  if (status === 'active' && startDate > today) return 'planned';
  return status;
}
